import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import authClient from "./UserManager"
import { useTranslation } from "react-i18next"
import i18n from "./i18n/i18n"
import { authLogger } from "./auth-logger"
import config from "./config"

const SUPPORTED_LANGUAGES = ["en", "fi", "se"] as const
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const RootHandler = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isCancelled = false

    const handleRootNavigation = async () => {
      // Check if this is an OAuth callback (has code or error params)
      const hasAuthParams = searchParams.has("code") || searchParams.has("error")

      if (hasAuthParams) {
        // This is an authentication callback
        try {
          // Retrieve the return URL BEFORE calling signinRedirectCallback
          // This prevents the issue where React Strict Mode double-invokes the effect
          const returnUrl = sessionStorage.getItem("returnUrl")

          const user = await authClient.signinRedirectCallback()
          authLogger.log(`✓ User authenticated: ${user.profile.email || user.profile.sub}`)

          // Only navigate if the effect hasn't been cancelled
          if (isCancelled) {
            return
          }

          // Remove returnUrl from sessionStorage after successful callback
          sessionStorage.removeItem("returnUrl")

          // Navigate to return URL or detect language and use default route
          if (returnUrl && returnUrl.startsWith("/")) {
            navigate(returnUrl, { replace: true })
          } else {
            // Detect language preference and navigate to the default enabled form
            const detectedLang = detectLanguage()
            const defaultFormKey = config.formDisabledRedirect
            const canonicalPath = i18n.getResource(
              detectedLang,
              "translation",
              `routes.${defaultFormKey}.canonical`,
            )
            navigate(`/${detectedLang}/${canonicalPath}`, { replace: true })
          }
        } catch (err) {
          authLogger.error(
            "✗ Authentication failed:",
            err instanceof Error ? err.message : String(err),
          )
          setError(t("auth.error"))
          setIsProcessing(false)
        }
      } else {
        // Not an auth callback - detect language and redirect
        if (!isCancelled) {
          const detectedLang = detectLanguage()
          const defaultFormKey = config.formDisabledRedirect
          const canonicalPath = i18n.getResource(
            detectedLang,
            "translation",
            `routes.${defaultFormKey}.canonical`,
          )
          navigate(`/${detectedLang}/${canonicalPath}`, { replace: true })
        }
      }
    }

    handleRootNavigation()

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true
    }
  }, [navigate, searchParams, t])

  const detectLanguage = (): SupportedLanguage => {
    // 1. Check localStorage for saved preference
    const savedLang = localStorage.getItem("preferredLanguage")
    if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang as SupportedLanguage)) {
      return savedLang as SupportedLanguage
    }

    // 2. Check browser language
    const browserLang = navigator.language.split("-")[0].toLowerCase()
    if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
      return browserLang as SupportedLanguage
    }

    // 3. Default to English
    return "en"
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    )
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>{t("auth.processing")}</div>
      </div>
    )
  }

  return null
}

export default RootHandler
